import React, { useState, useEffect, useRef } from "react";
import api from "../../services/api";
import IconPicker from "../../components/IconPicker";
import { Save, Plus, Trash2, X, Search, Tag, FileText, Upload, GripVertical, Image } from "lucide-react";

export default function AdminSettings() {
  const [tab, setTab] = useState("content");

  return (
    <div data-testid="admin-settings-page">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Settings</h1>
      <div className="flex gap-2 mb-6">
        <button onClick={() => setTab("content")}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${tab === "content" ? "bg-emerald-600 text-white" : "bg-white border border-gray-200 text-gray-600 hover:border-gray-300"}`}>
          Content & Pricing
        </button>
        <button onClick={() => setTab("pdf")}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${tab === "pdf" ? "bg-emerald-600 text-white" : "bg-white border border-gray-200 text-gray-600 hover:border-gray-300"}`}>
          PDF Template
        </button>
        <button onClick={() => setTab("nace")}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${tab === "nace" ? "bg-emerald-600 text-white" : "bg-white border border-gray-200 text-gray-600 hover:border-gray-300"}`}>
          NACE Codes
        </button>
      </div>
      {tab === "content" ? <ContentTab /> : tab === "pdf" ? <PdfTemplateTab /> : <NaceTab />}
    </div>
  );
}

function ContentTab() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    api.getContentSettings().then(setSettings).catch(console.error).finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true); setMsg("");
    try { await api.updateContentSettings(settings); setMsg("Saved!"); setTimeout(() => setMsg(""), 3000); }
    catch (err) { setMsg("Error: " + err.message); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="text-gray-400">Loading...</div>;
  if (!settings) return <div className="text-gray-400">No settings found</div>;

  const updateField = (f, v) => setSettings({ ...settings, [f]: v });
  const updatePricing = (f, v) => setSettings({ ...settings, pricing_settings: { ...settings.pricing_settings, [f]: v } });

  const inputCls = "w-full bg-white border border-gray-300 rounded-lg px-4 py-2.5 text-gray-900 outline-none focus:ring-2 focus:ring-emerald-500";

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <button onClick={handleSave} disabled={saving} data-testid="save-settings-btn"
          className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50">
          <Save className="w-4 h-4" /> {saving ? "Saving..." : "Save"}
        </button>
      </div>
      {msg && <div className={`px-4 py-2 rounded-lg text-sm ${msg.startsWith("Error") ? "bg-red-50 text-red-600" : "bg-green-50 text-green-600"}`}>{msg}</div>}
      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Landing Page</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm text-gray-500 mb-1">Hero Title</label>
            <input value={settings.landing_hero_title || ""} onChange={e => updateField("landing_hero_title", e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className="block text-sm text-gray-500 mb-1">Hero Subtitle</label>
            <input value={settings.landing_hero_subtitle || ""} onChange={e => updateField("landing_hero_subtitle", e.target.value)} className={inputCls} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-500 mb-1">"Why Choose Us" Section Title</label>
              <input value={settings.why_title || ""} placeholder="Why Choose Our Platform?"
                onChange={e => updateField("why_title", e.target.value)} className={inputCls} data-testid="why-title-input" />
            </div>
            <div>
              <label className="block text-sm text-gray-500 mb-1">"Why" Subtitle (optional)</label>
              <input value={settings.why_subtitle || ""}
                onChange={e => updateField("why_subtitle", e.target.value)} className={inputCls} />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-500 mb-1">Bottom CTA Title</label>
              <input value={settings.cta_title || ""} placeholder="Ready to find out where you stand?"
                onChange={e => updateField("cta_title", e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className="block text-sm text-gray-500 mb-1">Bottom CTA Subtitle</label>
              <input value={settings.cta_subtitle || ""} placeholder="Start any assessment for free..."
                onChange={e => updateField("cta_subtitle", e.target.value)} className={inputCls} />
            </div>
          </div>
          <div>
            <label className="block text-sm text-gray-500 mb-1">Footer "About" Text</label>
            <input value={settings.footer_about || ""}
              onChange={e => updateField("footer_about", e.target.value)} className={inputCls} />
          </div>
        </div>
      </div>

      {/* Why Features editor */}
      <div className="bg-white border border-gray-200 rounded-xl p-6" data-testid="why-features-editor">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">"Why Choose Us" Feature Cards</h2>
          <button onClick={() => updateField("why_features", [...(settings.why_features || []), { title: "", description: "", icon: "Shield", color: "emerald" }])}
            data-testid="add-why-feature-btn"
            className="flex items-center gap-1 text-emerald-600 hover:text-emerald-700 text-sm font-medium">
            <Plus className="w-4 h-4" /> Add feature
          </button>
        </div>
        {(settings.why_features || []).length === 0 ? (
          <p className="text-sm text-gray-400 italic">No features configured. Default 3 generic cards will be shown on the landing page.</p>
        ) : (
          <div className="space-y-3">
            {(settings.why_features || []).map((feat, idx) => (
              <div key={idx} className="bg-gray-50 border border-gray-200 rounded-lg p-3 space-y-2" data-testid={`why-feature-${idx}`}>
                <div className="flex items-center gap-2">
                  <input placeholder="Feature title" value={feat.title || ""}
                    onChange={e => {
                      const arr = [...(settings.why_features || [])];
                      arr[idx] = { ...arr[idx], title: e.target.value };
                      updateField("why_features", arr);
                    }}
                    className="flex-1 bg-white border border-gray-300 rounded px-3 py-1.5 text-sm text-gray-900 outline-none" />
                  <div className="w-36">
                    <IconPicker value={feat.icon || 'Shield'}
                      onChange={v => {
                        const arr = [...(settings.why_features || [])];
                        arr[idx] = { ...arr[idx], icon: v };
                        updateField("why_features", arr);
                      }}
                      testIdPrefix={`why-feature-icon-${idx}`} />
                  </div>
                  <select value={feat.color || "emerald"}
                    onChange={e => {
                      const arr = [...(settings.why_features || [])];
                      arr[idx] = { ...arr[idx], color: e.target.value };
                      updateField("why_features", arr);
                    }}
                    className="w-28 bg-white border border-gray-300 rounded px-3 py-1.5 text-sm text-gray-900 outline-none">
                    <option value="emerald">Emerald</option>
                    <option value="blue">Blue</option>
                    <option value="purple">Purple</option>
                    <option value="amber">Amber</option>
                    <option value="rose">Rose</option>
                  </select>
                  <button onClick={() => updateField("why_features", (settings.why_features || []).filter((_, i) => i !== idx))}
                    data-testid={`remove-why-feature-${idx}`} className="text-red-400 hover:text-red-600 p-1">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <textarea placeholder="Description" rows={2} value={feat.description || ""}
                  onChange={e => {
                    const arr = [...(settings.why_features || [])];
                    arr[idx] = { ...arr[idx], description: e.target.value };
                    updateField("why_features", arr);
                  }}
                  className="w-full bg-white border border-gray-300 rounded px-3 py-1.5 text-sm text-gray-900 outline-none resize-none" />
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Pricing (EUR)</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[["single_report_eur","Single Report"],["basic_plan_eur","Basic Plan"],["professional_plan_eur","Professional"],["enterprise_plan_eur","Enterprise"]].map(([k,l]) => (
            <div key={k}>
              <label className="block text-sm text-gray-500 mb-1">{l}</label>
              <input type="number" value={settings.pricing_settings?.[k] || 0} onChange={e => updatePricing(k, parseFloat(e.target.value))} className={inputCls} />
            </div>
          ))}
        </div>
      </div>

      {/* Demo payment toggle */}
      <div className="bg-white border border-gray-200 rounded-xl p-6" data-testid="demo-payment-panel">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <h2 className="text-lg font-semibold text-gray-900 mb-1">Demo Payment Mode</h2>
            <p className="text-sm text-gray-500">
              When enabled, clicking "Pay" on any assessment skips Stripe and immediately marks the
              report as paid. Use for internal demos, QA and pilot accounts — never enable in production.
            </p>
            {settings.demo_payment_mode && (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2 mt-3 inline-block">
                Demo payments are currently ACTIVE — all payments are fake.
              </p>
            )}
          </div>
          <label className="inline-flex items-center cursor-pointer shrink-0">
            <input type="checkbox" className="sr-only peer"
              data-testid="demo-payment-toggle"
              checked={Boolean(settings.demo_payment_mode)}
              onChange={async (e) => {
                const v = e.target.checked;
                const next = { ...settings, demo_payment_mode: v };
                setSettings(next);
                // Auto-persist immediately so admins don't need to click "Save" at the top.
                try { await api.updateContentSettings(next); } catch (err) { console.error(err); }
              }} />
            <div className="relative w-12 h-6 bg-gray-200 rounded-full peer-checked:bg-emerald-600 after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-6" />
            <span className="ml-2 text-sm font-medium text-gray-700">{settings.demo_payment_mode ? 'On' : 'Off'}</span>
          </label>
        </div>
      </div>
    </div>
  );
}

function PdfTemplateTab() {
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const fileRef = useRef();

  const ALL_SECTIONS = [
    { id: 'header', label: 'Header & Logo' },
    { id: 'company_info', label: 'Company Information' },
    { id: 'overall_score', label: 'Overall Score' },
    { id: 'category_scores', label: 'Category Scores' },
    { id: 'charts', label: 'Charts & Graphs' },
    { id: 'milestone_scores', label: 'Milestone Scores' },
    { id: 'detailed_answers', label: 'Detailed Answers' },
    { id: 'agent_comment', label: 'Agent Comment' },
    { id: 'footer', label: 'Footer' },
  ];

  useEffect(() => {
    api.getPdfTemplate().then(setTemplate).catch(console.error).finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true); setMsg("");
    try {
      await api.updatePdfTemplate(template);
      setMsg("Saved!");
      setTimeout(() => setMsg(""), 3000);
    } catch (err) { setMsg("Error: " + err.message); }
    finally { setSaving(false); }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { alert("Logo must be under 2MB"); return; }
    const reader = new FileReader();
    reader.onload = () => {
      const b64 = reader.result.split(",")[1];
      setTemplate({ ...template, logo_base64: b64 });
    };
    reader.readAsDataURL(file);
  };

  const moveSection = (idx, dir) => {
    const order = [...(template.section_order || [])];
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= order.length) return;
    [order[idx], order[newIdx]] = [order[newIdx], order[idx]];
    setTemplate({ ...template, section_order: order });
  };

  if (loading) return <div className="text-gray-400">Loading...</div>;
  if (!template) return <div className="text-gray-400">No template found</div>;

  const inputCls = "w-full bg-white border border-gray-300 rounded-lg px-4 py-2.5 text-gray-900 outline-none focus:ring-2 focus:ring-emerald-500";

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <button onClick={handleSave} disabled={saving} data-testid="save-pdf-template-btn"
          className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50">
          <Save className="w-4 h-4" /> {saving ? "Saving..." : "Save Template"}
        </button>
      </div>
      {msg && <div className={`px-4 py-2 rounded-lg text-sm ${msg.startsWith("Error") ? "bg-red-50 text-red-600" : "bg-green-50 text-green-600"}`}>{msg}</div>}

      {/* Logo */}
      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Image className="w-5 h-5 text-emerald-600" /> Company Logo
        </h2>
        <div className="flex items-center gap-4">
          {template.logo_base64 ? (
            <div className="relative">
              <img src={`data:image/png;base64,${template.logo_base64}`} alt="Logo" className="h-16 max-w-[200px] object-contain border border-gray-200 rounded-lg p-2" />
              <button onClick={() => setTemplate({ ...template, logo_base64: "" })}
                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-0.5"><X className="w-3 h-3" /></button>
            </div>
          ) : (
            <div className="h-16 w-40 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center text-gray-400 text-sm">
              No logo
            </div>
          )}
          <div>
            <input ref={fileRef} type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" />
            <button onClick={() => fileRef.current?.click()} data-testid="upload-logo-btn"
              className="flex items-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium transition-colors">
              <Upload className="w-4 h-4" /> Upload Logo
            </button>
            <p className="text-xs text-gray-400 mt-1">PNG or JPG, max 2MB</p>
          </div>
        </div>
      </div>

      {/* Header/Footer Text */}
      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <FileText className="w-5 h-5 text-emerald-600" /> Custom Text
        </h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm text-gray-500 mb-1">Header Text (appears below logo)</label>
            <input value={template.header_text || ""} onChange={e => setTemplate({ ...template, header_text: e.target.value })}
              className={inputCls} placeholder="e.g. Your Company Name - ESG Division" data-testid="pdf-header-text" />
          </div>
          <div>
            <label className="block text-sm text-gray-500 mb-1">Footer Text (appears at bottom)</label>
            <input value={template.footer_text || ""} onChange={e => setTemplate({ ...template, footer_text: e.target.value })}
              className={inputCls} placeholder="e.g. Confidential - For internal use only" data-testid="pdf-footer-text" />
          </div>
        </div>
      </div>

      {/* Section Order */}
      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <GripVertical className="w-5 h-5 text-emerald-600" /> Section Order
        </h2>
        <p className="text-sm text-gray-500 mb-3">Drag to reorder sections in the PDF report:</p>
        <div className="space-y-2">
          {(template.section_order || []).map((secId, idx) => {
            const sec = ALL_SECTIONS.find(s => s.id === secId) || { label: secId };
            return (
              <div key={secId} className="flex items-center gap-3 bg-gray-50 border border-gray-200 rounded-lg px-4 py-3"
                data-testid={`section-order-${secId}`}>
                <GripVertical className="w-4 h-4 text-gray-300 flex-shrink-0" />
                <span className="flex-1 text-sm text-gray-800">{sec.label}</span>
                <span className="text-xs text-gray-400 w-6 text-center">{idx + 1}</span>
                <button onClick={() => moveSection(idx, -1)} disabled={idx === 0}
                  className="text-gray-400 hover:text-gray-700 disabled:opacity-30 text-sm px-1">Up</button>
                <button onClick={() => moveSection(idx, 1)} disabled={idx === (template.section_order || []).length - 1}
                  className="text-gray-400 hover:text-gray-700 disabled:opacity-30 text-sm px-1">Down</button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function NaceTab() {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [newCode, setNewCode] = useState({ code: "", description: "", section: "" });

  const load = () => {
    setLoading(true);
    api.getAdminNaceCodes().then(setCodes).catch(console.error).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const filtered = codes.filter(c => {
    if (!search) return true;
    const s = search.toLowerCase();
    return c.code.toLowerCase().includes(s) || c.description.toLowerCase().includes(s) || (c.section || "").toLowerCase().includes(s);
  });

  const handleAdd = async () => {
    if (!newCode.code || !newCode.description) return;
    try {
      await api.createNaceCode(newCode);
      setNewCode({ code: "", description: "", section: "" });
      setShowAdd(false);
      load();
    } catch (err) { alert(err.message); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this NACE code?")) return;
    try { await api.deleteNaceCode(id); load(); } catch (err) { alert(err.message); }
  };

  const handleToggle = async (item) => {
    try { await api.updateNaceCode(item.id, { is_active: !item.is_active }); load(); }
    catch (err) { alert(err.message); }
  };

  const sections = [...new Set(codes.map(c => c.section).filter(Boolean))].sort();

  const inputCls = "w-full bg-white border border-gray-300 rounded-lg px-4 py-2.5 text-gray-900 placeholder-gray-400 outline-none focus:ring-2 focus:ring-emerald-500";

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Tag className="w-5 h-5 text-emerald-600" />
          <span className="text-gray-900 font-medium">{codes.length} NACE Codes</span>
        </div>
        <button onClick={() => setShowAdd(true)} data-testid="add-nace-btn"
          className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
          <Plus className="w-4 h-4" /> Add Code
        </button>
      </div>

      {showAdd && (
        <div className="fixed inset-0 bg-black/20 flex items-center justify-center z-50" onClick={() => setShowAdd(false)}>
          <div className="bg-white border border-gray-200 rounded-xl p-6 w-full max-w-md mx-4 shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-gray-900">Add NACE Code</h2>
              <button onClick={() => setShowAdd(false)} className="text-gray-400"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-3">
              <input placeholder="Code (e.g. 62.01)" value={newCode.code}
                onChange={e => setNewCode({ ...newCode, code: e.target.value })} className={inputCls} />
              <input placeholder="Description" value={newCode.description}
                onChange={e => setNewCode({ ...newCode, description: e.target.value })} className={inputCls} />
              <select value={newCode.section} onChange={e => setNewCode({ ...newCode, section: e.target.value })}
                className={inputCls}>
                <option value="">Select Section</option>
                {sections.map(s => <option key={s} value={s}>{s}</option>)}
                <option value="NEW">Other (New Section)</option>
              </select>
              <button onClick={handleAdd}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-medium py-2.5 rounded-lg transition-colors">Add</button>
            </div>
          </div>
        </div>
      )}

      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input type="text" value={search} onChange={e => setSearch(e.target.value)}
          className="w-full bg-white border border-gray-300 rounded-lg pl-10 pr-4 py-2.5 text-gray-900 placeholder-gray-400 outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
          placeholder="Search codes..." />
      </div>

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        {loading ? <div className="p-8 text-center text-gray-400">Loading...</div> : (
          <div className="max-h-[500px] overflow-auto">
            <table className="w-full">
              <thead className="sticky top-0 bg-gray-50">
                <tr className="border-b border-gray-200">
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Code</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Description</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Section</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.slice(0, 100).map(c => (
                  <tr key={c.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="px-4 py-2 text-sm font-mono text-emerald-600 font-bold">{c.code}</td>
                    <td className="px-4 py-2 text-sm text-gray-900">{c.description}</td>
                    <td className="px-4 py-2 text-sm text-gray-500">{c.section}</td>
                    <td className="px-4 py-2">
                      <button onClick={() => handleToggle(c)}
                        className={`text-xs px-2 py-1 rounded font-medium ${c.is_active ? "bg-green-50 text-green-600" : "bg-red-50 text-red-600"}`}>
                        {c.is_active ? "Active" : "Inactive"}
                      </button>
                    </td>
                    <td className="px-4 py-2 text-right">
                      <button onClick={() => handleDelete(c.id)} className="text-red-400 hover:text-red-600 p-1">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
                {filtered.length > 100 && (
                  <tr><td colSpan="5" className="px-4 py-3 text-center text-xs text-gray-400">Showing first 100 of {filtered.length} results</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
